import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getNotificationSettings,
  listNotifications,
  markAllRead,
  markRead,
  updateNotificationSettings,
} from './api';
import type { NotificationSettings } from './types';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  settings: () => [...notificationKeys.all, 'settings'] as const,
};

/** The grower's full notification feed + unread count (one fetch; panel sorts client-side). */
export function useNotifications() {
  return useQuery({ queryKey: notificationKeys.list(), queryFn: listNotifications });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}

/** The grower's notification delivery preferences (drawer, story 101880). */
export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationKeys.settings(),
    queryFn: getNotificationSettings,
  });
}

/**
 * Toggle a preference. Optimistically flips the cached flag so the switch responds
 * instantly, then reconciles with the server's authoritative response (and rolls back
 * on error) — the snappy, real-integration feel for a single boolean toggle.
 */
export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<NotificationSettings>) => updateNotificationSettings(patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: notificationKeys.settings() });
      const previous = qc.getQueryData<NotificationSettings>(notificationKeys.settings());
      if (previous) {
        qc.setQueryData<NotificationSettings>(notificationKeys.settings(), {
          ...previous,
          ...patch,
        });
      }
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) {
        qc.setQueryData(notificationKeys.settings(), context.previous);
      }
    },
    onSuccess: (settings) => {
      qc.setQueryData(notificationKeys.settings(), settings);
    },
  });
}
